import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Section from './shared/Section';

/* ── timeline data ── */
const milestones = [
  {
    year: '2022',
    title: 'The Intelligence Vision',
    body: 'OpticVance was founded by a team of AI engineers and enterprise data architects who believed predictive intelligence should be operational, not theoretical transforming fragmented enterprise data into structured, AI-driven decision systems capable of real-time analytical computation.',
    visual: 'brain',
    image: process.env.PUBLIC_URL + '/about us_result.webp',
    stats: [{ label: 'Founding AI Engineers', value: '4' }, { label: 'Seed Investment', value: '$2.5M' }],
    keywords: { 'predictive intelligence': 'Operational AI that converts raw data into actionable forecasts and decisions.' },
  },
  {
    year: '2023',
    title: 'Analytical Engine Launch',
    body: 'Our GPU-accelerated analytical intelligence engine launched publicly, enabling multi-source data ingestion, predictive modeling, and sub-second inference performance.',
    visual: 'chart',
    image: process.env.PUBLIC_URL + '/about us (2)_result.webp',
    stats: [{ label: 'Enterprise Deployments', value: '100+' }, { label: 'Countries', value: '12' }],
    keywords: { 'inference': 'Fast model execution that returns predictions in milliseconds for real-time decisioning.' },
  },
  {
    year: '2024',
    title: 'Scalable Intelligence Infrastructure',
    body: 'Series B funding accelerated global expansion and strengthened our AI compute architecture. We scaled predictive modeling frameworks, introduced real-time KPI monitoring systems, and achieved enterprise-grade security and compliance.',
    visual: 'globe',
    image: process.env.PUBLIC_URL + '/about us (3)_result.webp',
    stats: [{ label: 'AI & Data Engineers', value: '200+' }, { label: 'Series B Funding', value: '$40M' }],
    keywords: { 'KPI monitoring': 'Real-time dashboards and alerts that track model health and business impact.' },
  },
  {
    year: '2025',
    title: 'The Predictive Intelligence Era',
    body: 'We launched next-generation analytical models with advanced forecasting precision, low-latency inference pipelines, and scalable enterprise AI dashboards powering the predictive intelligence era for modern enterprises.',
    visual: 'zap',
    image: process.env.PUBLIC_URL + '/about us (4)_result.webp',
    stats: [{ label: 'Countries Served', value: '50+' }, { label: 'Enterprise Deployments', value: '200+' }],
    keywords: { 'forecasting': 'Advanced statistical and ML techniques that anticipate future outcomes with high confidence.' },
  },
];

const techTags = ['Machine Learning', 'Neural Networks', 'NLP', 'Computer Vision', 'Edge AI', 'Deep Learning'];

/* ── simple SVG visuals per milestone ── */
const visualMap = {
  brain: (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
      <circle cx="40" cy="40" r="28" className="stroke-primary/20" />
      <path d="M28 32c4-8 20-8 24 0M30 44c4 6 16 6 20 0" className="stroke-primary" />
      <circle cx="34" cy="38" r="2" className="fill-primary" />
      <circle cx="46" cy="38" r="2" className="fill-primary" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M16 60V20" className="stroke-primary/20" />
      <path d="M16 60h48" className="stroke-primary/20" />
      <path d="M22 48l12-16 10 8 14-20" className="stroke-primary" strokeWidth="2" />
      <circle cx="22" cy="48" r="2.5" className="fill-primary" />
      <circle cx="34" cy="32" r="2.5" className="fill-primary" />
      <circle cx="44" cy="40" r="2.5" className="fill-primary" />
      <circle cx="58" cy="20" r="2.5" className="fill-primary" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
      <circle cx="40" cy="40" r="26" className="stroke-primary/30" />
      <ellipse cx="40" cy="40" rx="14" ry="26" className="stroke-primary/20" />
      <path d="M14 40h52" className="stroke-primary/20" />
      <path d="M18 28h44M18 52h44" className="stroke-primary/10" />
      <circle cx="40" cy="40" r="4" className="fill-primary/30 stroke-primary" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 80 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M42 14L22 44h16l-4 22 20-30H38l4-22Z" className="stroke-primary fill-primary/10" />
    </svg>
  ),
};

/* ── Tooltip component ── */
const Tooltip = ({ term, definition }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline cursor-help"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
      tabIndex={0}
    >
      <span className="border-b border-dashed border-primary/50 text-primary/90">{term}</span>
      <AnimatePresence>
        {show && (
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 rounded-lg bg-dark-card border border-primary/20 text-xs text-gray-300 leading-relaxed shadow-xl shadow-black/50 pointer-events-none"
          >
            {definition}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
};

/* ── Render body text with keyword tooltips ── */
const renderBody = (text, keywords) => {
  if (!keywords || Object.keys(keywords).length === 0) return text;
  const regex = new RegExp(`(${Object.keys(keywords).join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) => {
    const matchKey = Object.keys(keywords).find((k) => k.toLowerCase() === part.toLowerCase());
    return matchKey ? <Tooltip key={i} term={part} definition={keywords[matchKey]} /> : part;
  });
};

/* ── Mobile accordion item ── */
const AccordionItem = ({ ms, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="glass-card rounded-xl overflow-hidden"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 p-5 text-left"
      >
        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-sm font-bold font-mono text-primary">
          {ms.year.slice(2)}
        </span>
        <span className="flex-1">
          <span className="text-white font-semibold">{ms.title}</span>
          <span className="block text-xs text-gray-500">{ms.year}</span>
        </span>
        <motion.svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="w-5 h-5 text-primary/50"
          animate={{ rotate: open ? 180 : 0 }}
        >
          <path d="M6 9l6 6 6-6" />
        </motion.svg>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5">
              <p className="text-gray-400 text-sm leading-relaxed mb-3">{renderBody(ms.body, ms.keywords)}</p>
              <div className="flex gap-4">
                {ms.stats.map((s) => (
                  <div key={s.label} className="text-center">
                    <span className="block text-lg font-bold gradient-text">{s.value}</span>
                    <span className="text-[11px] text-gray-500">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ── Main component ── */
const AboutUs = () => {
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start end', 'end start'],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <Section id="about" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Centered intro ── */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block px-4 py-1 rounded-full text-xs font-mono tracking-widest uppercase border border-primary/30 text-primary mb-4">
            About Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Engineering Predictive Intelligence{' '}
            <span className="gradient-text">or Modern Enterprises</span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Opticvance was founded on a simple but urgent realization,
businesses are overwhelmed with data, yet underpowered in insight.
          </p>
        </div>

        {/* ── Desktop zigzag timeline ── */}
        <div ref={timelineRef} className="relative hidden lg:block">
          {/* Animated dotted center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2" aria-hidden="true">
            <div className="absolute inset-0 border-l-2 border-dashed border-primary/10" />
            <motion.div
              className="absolute top-0 left-0 w-full border-l-2 border-dashed border-primary/50"
              style={{ height: lineHeight }}
            />
          </div>

          {milestones.map((ms, i) => {
            const isLeft = i % 2 === 0;
            return (
              <motion.div
                key={ms.year}
                className={`relative flex items-center mb-24 last:mb-0 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
                initial={{ opacity: 0, x: isLeft ? -60 : 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, delay: 0.1 }}
              >
                {/* Content side */}
                <div className={`w-[45%] ${isLeft ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                  <span className="text-xs font-mono text-primary/60 tracking-widest uppercase">{ms.year}</span>
                  <h3 className="text-2xl font-bold mt-1 mb-3">{ms.title}</h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    {renderBody(ms.body, ms.keywords)}
                  </p>
                  <div className={`flex gap-6 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                    {ms.stats.map((s) => (
                      <div key={s.label}>
                        <span className="block text-xl font-bold gradient-text">{s.value}</span>
                        <span className="text-xs text-gray-500">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Center node */}
                <div className="absolute left-1/2 -translate-x-1/2 z-10">
                  <div className="w-12 h-12 rounded-full bg-dark border-2 border-primary/40 flex items-center justify-center shadow-lg shadow-primary/10">
                    <span className="text-sm font-bold font-mono gradient-text">{ms.year.slice(2)}</span>
                  </div>
                </div>

                {/* Visual side */}
                <div className={`w-[45%] ${isLeft ? 'pl-12' : 'pr-12'}`}>
                  <div
                    className="glass-card rounded-2xl p-8 relative overflow-hidden group h-56 lg:h-72"
                    style={ms.image ? { backgroundImage: `url("${encodeURI(ms.image)}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                  >
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                    <div className="relative z-10 flex items-center justify-center">
                      {!ms.image && (
                        <div className="w-32 h-32 mx-auto opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 rounded-lg">
                          {visualMap[ms.visual]}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Mobile accordion view ── */}
        <div className="lg:hidden space-y-4">
          {milestones.map((ms, i) => (
            <AccordionItem key={ms.year} ms={ms} index={i} />
          ))}
        </div>

        {/* ── Tech tags ── */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mt-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {techTags.map((tag) => (
            <span
              key={tag}
              className="px-4 py-1.5 bg-primary/5 border border-primary/15 rounded-full text-sm text-primary/80 hover:bg-primary/10 hover:border-primary/30 transition-colors cursor-default"
            >
              {tag}
            </span>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

export default AboutUs;
